import { useState, useEffect, useCallback } from "react";
import useShouldShowInstallPwaPrompt from "./useShouldShowInstallPwaPrompt";

// Note: Although highly modified, the basic idea for this hook came from the following article:
// https://blog.wick.technology/pwa-install-prompt/

type PromptResult = {
    userChoice: "accepted" | "dismissed",
    platform: string;
};

type ChoiceResult = {
    outcome: "accepted" | "dismissed";
};

type BeforeInstallPromptEvent = {
    platform: string[];
    userChoice: Promise<ChoiceResult>;
    prompt: () => Promise<PromptResult>;
} & Event;

interface InstallPromptEventMap {
    "beforeinstallprompt": BeforeInstallPromptEvent;
}

declare global {
    interface Window {
        addEventListener<K extends keyof InstallPromptEventMap>(type: K,
            listener: (this: Window, ev: InstallPromptEventMap[K]) => void): void;
        removeEventListener<K extends keyof InstallPromptEventMap>(type: K,
            listener: (this: Window, ev: InstallPromptEventMap[K]) => void): void;
        dispatchEvent<K extends keyof InstallPromptEventMap>(ev: InstallPromptEventMap[K]): boolean;
    }

    interface Navigator {
        standalone: boolean;
    }
}

export default function useInstallPwaPromptState(defaultPromptIntervalMinutes: number): [
    boolean,
    () => void, // install
    () => void, // postpone
    () => void  // disable
] {
    // console.log("Rendering useInstallPwaPromptState");

    const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent>();
    const [
        showInstallPwaPrompt,
        setInstallPwaPromptShown
    ] = useShouldShowInstallPwaPrompt(defaultPromptIntervalMinutes);

    useEffect(() => {

        function beforeInstallPromptHandler(event: BeforeInstallPromptEvent) {
            event.preventDefault();

            // console.log(`**** beforeInstallPromptHandler showInstallPwaPrompt=${showInstallPwaPrompt}`);

            if (showInstallPwaPrompt) {
                // console.log(`**** beforeInstallPromptHandler setInstallPromptEvent`);
                setInstallPromptEvent(event);
            }
        };

        window.addEventListener("beforeinstallprompt", beforeInstallPromptHandler);

        return function cleanuo() {
            window.removeEventListener("beforeinstallprompt", beforeInstallPromptHandler);
        };

    }, [showInstallPwaPrompt]);

    const install = useCallback(async () => {
        // show native prompt
        await installPromptEvent?.prompt();

        // retrive whether the user accepted or declined the install
        const result = await installPromptEvent?.userChoice;

        if (result?.outcome === "accepted") {
            setInstallPwaPromptShown(new Date(), false);

        } else if (result?.outcome === "dismissed") {
            // consider this the same as "don't remind me again"
            setInstallPwaPromptShown(new Date(), true);
        }

        setInstallPromptEvent(undefined);
    }, [
        installPromptEvent,
        setInstallPwaPromptShown
    ]);

    const postpone = useCallback(() => {
        setInstallPwaPromptShown(new Date(), false);
    }, [setInstallPwaPromptShown]);

    const disable = useCallback(() => {
        setInstallPwaPromptShown(new Date(), true);
    }, [setInstallPwaPromptShown]);

    const showPrompt = showInstallPwaPrompt &&
        (!window.hasOwnProperty("BeforeInstallPromptEvent") || !!installPromptEvent);

    return [
        showPrompt,
        install,
        postpone,
        disable
    ];
};